<template>
  <!-- DISABLED IS NOT ABSENT, the same rule the three levers beside this follow: a channel whose
       curve the payload does not carry yet has the bar and the five figures on the screen, greyed,
       so answering the two fields turns a control on where it already stood. -->
  <div class="zones" :class="{ 'zones--disabled': disabled, 'zones--readonly': readonly }">
    <!-- THE BAR. One continuous run of the five colours the chart above shades in, with the four
         boundaries as grips on it — so where a zone begins is dragged where it is READ, and not
         transcribed into a field that stands somewhere else. It is deaf to the pointer everywhere
         except on a grip: a bar that jumps to wherever it was clicked would move a boundary on the
         way to reading one.

         READONLY IS THE SAME PICTURE WITH NOTHING TO TAKE HOLD OF. The bands are where the five
         zones fall and that is worth drawing whether or not they may be moved, so the run of colour
         and the five figures under it stay exactly as they are and everything that said they were
         controls goes: no grips on the bar, no fields under it, and the pointer handed back to the
         page it is scrolling. -->
    <div
      ref="bar"
      class="zones__bar"
      @pointermove="onMove"
      @pointerup="onUp"
      @pointercancel="onUp">
      <span
        v-for="band in bands"
        :key="band.key"
        class="zones__band"
        :class="{ 'zones__band--open': !band.fill }"
        :style="band.style"
        :title="band.title"></span>
      <button
        v-for="grip in handles"
        :key="grip.key"
        type="button"
        class="zones__grip"
        :class="{
          'zones__grip--active': dragging === grip.index,
          'zones__grip--locked': grip.locked
        }"
        :style="grip.style"
        :disabled="disabled || grip.locked"
        role="slider"
        :aria-label="grip.hint"
        :aria-valuemin="0"
        :aria-valuemax="axis"
        :aria-valuenow="grip.value"
        :aria-valuetext="grip.text"
        :title="grip.hint"
        @pointerdown="onDown(grip.index, $event)"
        @keydown="onKey(grip.index, $event)"></button>
    </div>

    <!-- And the same five cells the key has always been, with the figures now the field they are
         set in. Equal cells and not cells the width of their own zone: that is what the bar above
         is for, and a column the width of the first zone could not hold the number that belongs in
         it. The first begins at nothing — the axis has nowhere further left to go — so it is always
         a reading; a LOCKED one is a reading for as long as it is locked, drawn as the same plain
         figure rather than as a field that would refuse what was typed into it. -->
    <div class="zones__row">
      <div v-for="cell in cells" :key="cell.key" class="zones__cell">
        <input
          v-if="cell.index !== null && !cell.locked && !readonly"
          class="zones__field"
          type="text"
          inputmode="decimal"
          :value="cell.field"
          :disabled="disabled"
          :aria-label="cell.aria"
          :title="cell.aria"
          @input="onType(cell.index, $event.target.value)"
          @keydown.enter.prevent="commitField(cell.index)"
          @blur="commitField(cell.index)">
        <span v-else class="zones__fixed" :title="cell.hint">{{ cell.field }}</span>
        <span class="zones__name" :title="cell.title">{{ cell.label }}</span>
        <span
          class="zones__rule"
          :class="{ 'zones__rule--open': !cell.fill }"
          :style="cell.fill ? { background: cell.fill } : null"></span>
      </div>
    </div>
  </div>
</template>

<script>
// THE FIVE OPTIMAL ZONES OF ONE CHANNEL, AS A THING THAT CAN BE MOVED.
//
// It owns geometry and a pointer and nothing else. What a boundary MEANS — that the four of them
// come off two free numbers, so that dragging one has to say what it holds still — is the wizard's,
// and it lives in hillRegionSet where a test can reach it; a component cannot be mounted in this
// project (vue and vue-template-compiler are a patch apart, so @vue/test-utils refuses to load), so
// anything that can be pinned by a test is somewhere else on principle.
//
// The projection is the one decision here worth a paragraph. Drawn honestly to scale, three of the
// five zones are unreadable: the fifth runs to the edge of the plot, the fourth takes most of what
// is left, and on a gently turning curve the first three are a percent of the width between them —
// which is why this key was five equal cells before it was a bar at all. So every zone is given a
// floor of ZONE_FLOOR of the width and the REST is shared out in true proportion. It is a
// deliberate distortion of one axis and it is the only one in this editor: the chart above is drawn
// straight, and it is the chart that is the picture of the curve. This is a control, and a control
// has to be reachable at every zone the curve has.
const ZONE_COUNT = 5

// The share of the bar every zone is given before proportion is applied, and what is left over for
// proportion to spend. Eight percent of five hundred pixels is forty — a band, not a hairline —
// and the three fifths that remain are still enough for the fourth zone to read as the widest thing
// on the bar, which it almost always is.
const ZONE_FLOOR = 8
const ZONE_SPREAD = 100 - ZONE_COUNT * ZONE_FLOOR

// What an arrow key moves a boundary by, as a share of the axis. A keyboard has no release, so each
// press is its own commit — the same as the seasonality editor, and for the same reason.
const KEY_STEP = 0.01
const KEY_STEP_COARSE = 0.05

export default {
  name: 'SaturationZones',
  props: {
    // The five zones as the wizard names them:
    // { key, label, title, fill, field, aria, handle, locked, held }.
    // `field` is the spend the zone begins at, already through the wizard's own formatting — only
    // the wizard knows what any of this is denominated in. `locked` is the wizard saying that this
    // zone's left edge is the thing it is currently holding still, and `held` is what to say about
    // it: WHICH boundary that is, and why, is a statement about the curve and is not this
    // component's to make. The grip stays on the bar and stops being a grip.
    zones: { type: Array, required: true },
    // The four boundaries in the wizard's own units, ascending. hillRegions, and the same array the
    // chart above shades from.
    bounds: { type: Array, required: true },
    // The top of the axis the bar is drawn across. Held still by the wizard for as long as the
    // boundaries stay inside it, so a grip dragged along it does not drag the ruler with it.
    axis: { type: Number, required: true },
    // (text) => number, for a figure typed into a cell. NaN or anything not positive is refused by
    // putting the field back to what the page says, which is what the two figures beside the chart
    // do with a number they cannot take.
    parseValue: { type: Function, required: true },
    disabled: { type: Boolean, default: false },
    // The bar as a reading and nothing else: the wizard's simple mode, where a channel is stated by
    // its two figures and the zones are what those two figures came to. Not the same thing as
    // `disabled`, which is a control that is momentarily unusable and greys itself to say so — this
    // is a control that is not being offered at all, and it looks like the caption it is.
    readonly: { type: Boolean, default: false }
  },
  data () {
    return {
      // Which boundary is under the pointer, and where the axis stood when it was taken hold of:
      // solving a boundary moves the others, and a ruler that re-fitted mid-gesture would move the
      // ground under the grip being dragged.
      dragging: null,
      frozen: 0,
      // The distance between the grip and the pointer when it was grabbed, so a grip taken by its
      // edge does not jump its own width on the first frame.
      grab: 0,
      // The last thing the drag asked for, which is what the release commits: the pointer's own
      // position is read once per frame and there is no reason to read it again at the end.
      last: 0,
      // What is being typed, per cell, until it is committed or abandoned. A field is also a
      // reading, and it goes back to being one the moment the boundaries move under it.
      typing: {}
    }
  },
  computed: {
    // The axis in force this frame: the wizard's, or the one frozen for the length of a gesture.
    scale () {
      const held = this.dragging === null ? Number(this.axis) : this.frozen
      return held > 0 ? held : 0
    },
    // The six edges of the five bands, in the wizard's units, held inside the axis and ascending —
    // a boundary the axis has not caught up with yet lands on the end of it rather than off the bar.
    edges () {
      const top = this.scale
      const out = [0]
      for (let i = 0; i < ZONE_COUNT - 1; i++) {
        const at = Number(this.bounds[i])
        const held = isFinite(at) && at > 0 ? Math.min(at, top) : 0
        out.push(Math.max(held, out[out.length - 1]))
      }
      out.push(top)
      return out
    },
    // Every zone's floor plus its true share of what is left, which sums to a hundred by
    // construction: the floors are ZONE_COUNT * ZONE_FLOOR and the shares are the rest.
    widths () {
      const top = this.scale
      const edges = this.edges
      return edges.slice(1).map((edge, index) => {
        const span = top > 0 ? (edge - edges[index]) / top : 0
        return ZONE_FLOOR + ZONE_SPREAD * span
      })
    },
    // Where each edge lands on the bar, as a percentage. The inverse of this is what a drag is read
    // through, so the two are one list rather than two formulas.
    offsets () {
      const out = [0]
      this.widths.forEach(width => out.push(out[out.length - 1] + width))
      return out
    },
    bands () {
      const offsets = this.offsets
      return this.zones.map((zone, index) => ({
        key: zone.key,
        fill: zone.fill,
        title: zone.title,
        style: {
          left: `${offsets[index]}%`,
          width: `${this.widths[index]}%`,
          background: zone.fill || undefined
        }
      }))
    },
    // What is actually drawn on the bar: the grips, or none of them. A readonly bar is the same
    // picture with nothing on it to take hold of.
    handles () {
      return this.readonly ? [] : this.grips
    },
    // One grip per boundary, which is one per zone except the first: a zone that begins at nothing
    // has no left edge to take hold of.
    grips () {
      const offsets = this.offsets
      return this.zones.slice(1).map((zone, index) => ({
        key: `grip_${zone.key}`,
        index: index,
        locked: !!zone.locked,
        // What the pointer is told this edge is. A locked one says what is holding it rather than
        // what dragging it would do, that being the one question a reader has when a handle on a
        // bar full of handles does not take.
        hint: zone.locked ? zone.held : zone.handle,
        text: zone.field,
        value: Number(this.bounds[index]) || 0,
        style: { left: `${offsets[index + 1]}%` }
      }))
    },
    cells () {
      return this.zones.map((zone, index) => ({
        key: zone.key,
        // The first zone's figure is a reading; the other four are the boundary below them.
        index: index === 0 ? null : index - 1,
        locked: !!zone.locked,
        fill: zone.fill,
        label: zone.label,
        title: zone.title,
        aria: zone.aria,
        // What is holding a boundary still is a fact about the gesture that would move it, so it is
        // not said where there is no gesture: a readonly cell is captioned by its own zone.
        hint: zone.locked && !this.readonly ? zone.held : zone.title,
        field: index > 0 && Object.prototype.hasOwnProperty.call(this.typing, index - 1)
          ? this.typing[index - 1]
          : zone.field
      }))
    }
  },
  watch: {
    // The boundaries moved, so every field is a reading of them again: a draft held past the move
    // it was answered by would caption a bar that is no longer there.
    bounds () {
      this.typing = {}
    }
  },
  methods: {
    // A percentage of the bar back into the wizard's own units, through the same piecewise map the
    // bands are drawn with — inside the zone the pointer is in, which is what makes the floor under
    // a narrow zone a real place to drag through rather than a dead one.
    valueAt (percent) {
      const offsets = this.offsets
      const edges = this.edges
      const held = Math.max(0, Math.min(100, percent))
      let zone = 0
      while (zone < ZONE_COUNT - 1 && held > offsets[zone + 1]) zone++
      const width = this.widths[zone]
      const share = width > 0 ? (held - offsets[zone]) / width : 0
      return edges[zone] + share * (edges[zone + 1] - edges[zone])
    },
    pointerPercent (event) {
      const box = this.$refs.bar.getBoundingClientRect()
      if (!(box.width > 0)) return null
      return 100 * (event.clientX - box.left) / box.width
    },
    // A locked grip is a disabled button and never fires either of these; they are asked anyway,
    // because what may be moved is not something to be checked in one place and assumed in two.
    locked (index) {
      const zone = this.zones[index + 1]
      return !!(zone && zone.locked)
    },
    onDown (index, event) {
      if (this.disabled || this.locked(index)) return
      const at = this.pointerPercent(event)
      if (at === null) return
      // The ruler is held still for the whole gesture. Every boundary moves when one of them is
      // solved, and the axis is a function of the last of them.
      this.frozen = Number(this.axis)
      this.dragging = index
      this.grab = this.offsets[index + 1] - at
      this.last = 0
      if (this.$refs.bar.setPointerCapture) this.$refs.bar.setPointerCapture(event.pointerId)
      event.preventDefault()
    },
    // Nothing is clamped here beyond the ends of the bar. Where a boundary can actually GO is a
    // statement about the curve — the exponent has a range, and the other three boundaries travel
    // with this one — so the grip follows the answer that came back rather than the pointer, and it
    // stops where the shape stops.
    onMove (event) {
      if (this.dragging === null) return
      const at = this.pointerPercent(event)
      if (at === null) return
      const value = this.valueAt(at + this.grab)
      if (!(value > 0)) return
      this.last = value
      this.$emit('preview', this.dragging, value)
    },
    // A grip pressed and let go without travelling commits nothing: it was read, not moved.
    onUp () {
      if (this.dragging === null) return
      const index = this.dragging
      const value = this.last
      this.dragging = null
      this.frozen = 0
      this.grab = 0
      this.last = 0
      if (value > 0) this.$emit('input', index, value)
    },
    onKey (index, event) {
      if (this.disabled || this.locked(index)) return
      const step = (event.shiftKey ? KEY_STEP_COARSE : KEY_STEP) * Number(this.axis)
      let delta = 0
      if (event.key === 'ArrowRight' || event.key === 'ArrowUp') delta = step
      if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') delta = -step
      if (delta === 0) return
      const value = Number(this.bounds[index]) + delta
      event.preventDefault()
      if (value > 0) this.$emit('input', index, value)
    },
    onType (index, text) {
      this.$set(this.typing, index, text)
    },
    // Committed on blur and on Enter, like every other figure in this editor, and refused the same
    // way: a field that is not a positive amount goes back to what the bar says. A field nobody
    // typed in commits NOTHING — the figure on screen is rounded for reading and committing it
    // back would walk the boundary a little every time the cursor passed through.
    commitField (index) {
      if (!Object.prototype.hasOwnProperty.call(this.typing, index)) return
      const text = this.typing[index]
      this.$delete(this.typing, index)
      const zone = this.zones[index + 1]
      if (!zone || String(text) === String(zone.field)) return
      const value = this.parseValue(text)
      if (isFinite(value) && value > 0) this.$emit('input', index, value)
    }
  }
}
</script>

<style scoped>
  .zones--disabled {
    opacity: 0.55;
  }

  /* Nothing on the bar takes a pointer, so the bar gives the pointer back: `touch-action: none` is
     there to stop a drag scrolling the dialog, and there is nothing here to drag. */
  .zones--readonly .zones__bar {
    touch-action: auto;
  }

  /* The bar itself. A hairline around the whole run rather than around each band, the five report
     colours being pale enough that a border per band would read as four extra boundaries. */
  .zones__bar {
    position: relative;
    height: 26px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    overflow: visible;
    touch-action: none;
    background: #fff;
  }

  .zones__band {
    position: absolute;
    top: 0;
    bottom: 0;
    /* Two bands on a shared edge arrive as a pale seam at fractional pixel widths, which would read
       as a boundary of its own. */
    shape-rendering: crispEdges;
  }

  .zones__band:first-child {
    border-top-left-radius: 3px;
    border-bottom-left-radius: 3px;
  }

  .zones__band:last-child {
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
  }

  /* The first zone is the range nothing is painted over, which is the report's own convention and
     the chart's: below the minimum spending threshold there is nothing being bought worth colouring
     in. Hatched rather than empty so the bar still reads as running from zero. */
  .zones__band--open {
    background: repeating-linear-gradient(
      45deg,
      rgba(0, 0, 0, 0.05),
      rgba(0, 0, 0, 0.05) 3px,
      transparent 3px,
      transparent 6px
    );
  }

  /* Taller than the bar and centred on its edge, so the grip is a handle ON the boundary rather
     than a marker beside it, and so there is something to take hold of where two boundaries are
     nearly on top of each other. */
  .zones__grip {
    position: absolute;
    top: -4px;
    bottom: -4px;
    width: 13px;
    margin-left: -6.5px;
    padding: 0;
    border: 1px solid rgba(0, 0, 0, 0.35);
    border-radius: 3px;
    background: #fff;
    cursor: ew-resize;
    touch-action: none;
    /* The two hairlines of a grip, which is what says it is a grip and not a divider. */
    background-image: linear-gradient(
      to right,
      transparent 3px,
      rgba(0, 0, 0, 0.32) 3px,
      rgba(0, 0, 0, 0.32) 4px,
      transparent 4px,
      transparent 7px,
      rgba(0, 0, 0, 0.32) 7px,
      rgba(0, 0, 0, 0.32) 8px,
      transparent 8px
    );
  }

  .zones__grip:focus {
    outline: none;
    border-color: #1976D2;
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.28);
  }

  /* A held boundary keeps its place on the bar and gives up everything that said it was a handle:
     the two grooves go, and the outline goes dashed. It is still the edge of a zone — dropping it
     would leave the fifth zone starting at nothing — and it is no longer a thing to take hold of. */
  .zones__grip--locked {
    border-style: dashed;
    border-color: rgba(0, 0, 0, 0.22);
    background-image: none;
  }

  .zones__grip:hover:not(:disabled),
  .zones__grip--active {
    border-color: rgba(0, 0, 0, 0.6);
  }

  .zones__grip:disabled {
    cursor: default;
  }

  .zones__row {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 10px;
    margin-top: 8px;
  }

  .zones__cell {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  /* Which band this cell is the caption of. The bar above is where the widths are said, so this is
     a tie and not a scale. It sits UNDER the figure and the name rather than over them: the cell
     reads top to bottom as the two things it says and then the colour they belong to, and an
     underline is what a caption is tied to its band by. */
  .zones__rule {
    height: 3px;
    border-radius: 2px;
    margin-top: 5px;
  }

  /* Zone 1 has no fill on the bar — below the minimum spending threshold there is nothing bought
     worth colouring in — so its caption is tied to nothing and carries no rule at all. It kept the
     cell's height so the five captions still sit on one line. */
  .zones__rule--open {
    background: transparent;
  }

  /* The figure, and the field are the same object: it is typed where it is read, in the type it is
     read in, with a dotted rule under it as the only thing saying it can be. */
  .zones__field,
  .zones__fixed {
    font-size: 0.84rem;
    font-weight: 500;
    line-height: 1.3;
    color: rgba(0, 0, 0, 0.8);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .zones__field {
    width: 100%;
    border: 0;
    border-bottom: 1px dashed rgba(0, 0, 0, 0.28);
    border-radius: 0;
    background: transparent;
    padding: 0 0 1px;
    outline: none;
  }

  .zones__field:focus {
    border-bottom: 1px solid #1976D2;
  }

  .zones__field:disabled {
    border-bottom-color: transparent;
    color: rgba(0, 0, 0, 0.8);
  }

  /* The one figure here that is not a control: the first zone begins where the axis does. */
  .zones__fixed {
    border-bottom: 1px solid transparent;
    padding: 0 0 2px;
  }

  .zones__name {
    font-size: 0.68rem;
    line-height: 1.4;
    color: rgba(0, 0, 0, 0.45);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
